/**
 * DeviceHealthDetail Page
 *
 * Displays comprehensive health monitoring information for a single device
 * Includes:
 * - Overall health status
 * - WebSocket connection status
 * - API connectivity status
 * - Stripe Terminal reader status
 * - App information (version, OS)
 * - Recent errors
 * - Auto-refresh every 30 seconds
 */

/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DeviceHealthBadge from '../components/DeviceHealthBadge';
import HealthStatusLegend from '../components/HealthStatusLegend';
import apiService from '../services/apiService';

const DeviceHealthDetail = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  const [health, setHealth] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({
    title: '',
    message: '',
    type: 'info',
    duration: 0,
    dismissible: true
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [forcingUpdateCheck, setForcingUpdateCheck] = useState(false);

  useEffect(() => {
    loadHealthData();

    // Auto-refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadHealthData(true); // Silent refresh
      }, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [deviceId, autoRefresh]);

  const loadHealthData = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);

    try {
      const healthData = await apiService.getDeviceHealth(deviceId);
      setHealth(healthData);

      // Extract device info from health data
      if (healthData.device) {
        setDeviceInfo(healthData.device);
      }

      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error loading device health:', err);
      setError('Impossibile caricare i dati di salute del device');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Mai';
    const date = new Date(timestamp);
    return date.toLocaleString('it-IT');
  };

  const formatDuration = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Pochi secondi fa';
    if (diffMins < 60) return `${diffMins} minuti fa`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} ore fa`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} giorni fa`;
  };

  const getConnectionStatusColor = (status) => {
    switch (status) {
      case 'ONLINE': return '#28a745';
      case 'DEGRADED': return '#ffc107';
      case 'OFFLINE': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const handleOpenMessageModal = () => {
    setMessageForm({
      title: '',
      message: '',
      type: 'info',
      duration: 0,
      dismissible: true
    });
    setShowMessageModal(true);
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
  };

  const handleSendMessage = async () => {
    if (!messageForm.title || !messageForm.message) {
      alert('Titolo e messaggio sono obbligatori');
      return;
    }

    try {
      setSendingMessage(true);
      const result = await apiService.sendDisplayMessage(deviceId, messageForm);

      if (result.success) {
        alert(`✅ Messaggio inviato con successo a ${health.deviceLabel || 'device'}`);
        handleCloseMessageModal();
      } else {
        alert(`❌ Errore: ${result.error}`);
      }
    } catch (err) {
      alert(`❌ Errore nell'invio: ${err.message}`);
    } finally {
      setSendingMessage(false);
    }
  };

  const canSendMessage = () => {
    if (!health) return false;
    const wsStatus = health.websocket?.status;
    return wsStatus === 'ONLINE';
  };

  const handleForceUpdateCheck = async () => {
    if (!canSendMessage()) {
      alert('❌ Device non connesso al WebSocket: impossibile forzare il check update');
      return;
    }

    try {
      setForcingUpdateCheck(true);

      const result = await apiService.sendWebSocketCommand(deviceId, {
        event: 'force_update_check',
        data: {
          source: 'admin-webapp',
          requestedAt: new Date().toISOString(),
          forceCheck: true,
        },
      });

      if (result.success) {
        alert('✅ Comando inviato: il device eseguirà subito il check aggiornamenti');
      } else {
        alert(`❌ Errore invio comando: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`❌ Errore invio comando: ${err.message}`);
    } finally {
      setForcingUpdateCheck(false);
    }
  };

  const handleRebootDevice = async () => {
    if (!canSendMessage()) {
      alert('❌ Device non connesso al WebSocket: impossibile riavviare il tablet');
      return;
    }

    const confirmed = window.confirm(
      `🔄 CONFERMA RIAVVIO\n\n` +
      `Sei sicuro di voler riavviare il tablet?\n\n` +
      `Device: ${health?.deviceLabel || 'N/A'}\n` +
      `Setup Code: ${health?.setupCode || 'N/A'}\n\n` +
      `Il tablet si riavvierà immediatamente e sarà offline per ~60 secondi.`
    );

    if (!confirmed) return;

    const reason = window.prompt(
      'Motivo del riavvio (opzionale):',
      'Manutenzione da admin webapp'
    );

    try {
      const result = await apiService.rebootDevice(deviceId, reason || 'Admin reboot');
      
      if (result.success) {
        alert(
          `✅ Comando di riavvio inviato con successo!\n\n` +
          `Il tablet si riavvierà tra pochi secondi.\n\n` +
          `Nota: Il device sarà offline per circa 60 secondi durante il riavvio.\n` +
          `La pagina si aggiornerà automaticamente.`
        );
        
        // Auto-refresh dopo 3 secondi per vedere il device andare offline
        setTimeout(() => {
          loadHealthData();
        }, 3000);
      } else {
        alert(`❌ Errore: ${result.error}`);
      }
    } catch (err) {
      alert(`❌ Errore durante il riavvio: ${err.message}`);
    }
  };

  const getOverallHealthTooltip = (status) => {
    switch (status) {
      case 'HEALTHY':
        return 'Tutto funziona correttamente: WebSocket e API online, lettore connesso, nessun errore';
      case 'DEGRADED':
        return 'Performance degradate: uno o entrambi i canali (WebSocket/API) hanno heartbeat rallentati (2-10 min)';
      case 'WARNING':
        return 'Attenzione: un canale offline oppure lettore disconnesso, ma il device è ancora parzialmente raggiungibile';
      case 'ERROR':
        return 'Errore rilevato: il device ha segnalato errori recenti, controllare la sezione "Ultimo Errore"';
      case 'OFFLINE':
        return 'Device completamente offline: nessun heartbeat da oltre 10 minuti su entrambi i canali';
      case 'UNKNOWN':
      default:
        return 'Stato sconosciuto: il device non ha mai inviato dati o non è mai stato connesso';
    }
  };

  const getConnectionStatusTooltip = (status) => {
    switch (status) {
      case 'ONLINE':
        return 'ONLINE: Ultimo heartbeat ricevuto meno di 2 minuti fa - connessione attiva e funzionante';
      case 'DEGRADED':
        return 'DEGRADED: Ultimo heartbeat tra 2-10 minuti fa - connessione rallentata, possibile app in background o standby';
      case 'OFFLINE':
        return 'OFFLINE: Nessun heartbeat da oltre 10 minuti - connessione persa o device spento';
      case 'UNKNOWN':
      default:
        return 'UNKNOWN: Mai connesso o dati non disponibili';
    }
  };

  const getWifiSignalQuality = (dbm) => {
    if (dbm >= -50) return { label: 'Eccellente', color: '#28a745', icon: '🟢' };
    if (dbm >= -60) return { label: 'Ottimo', color: '#5cb85c', icon: '🟢' };
    if (dbm >= -70) return { label: 'Buono', color: '#5bc0de', icon: '🔵' };
    if (dbm >= -80) return { label: 'Discreto', color: '#f0ad4e', icon: '🟡' };
    if (dbm >= -90) return { label: 'Debole', color: '#d9534f', icon: '🔴' };
    return { label: 'Molto Debole', color: '#d9534f', icon: '🔴' };
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
            <p>Caricamento stato salute device...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>❌ Errore</h2>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/admin', { state: { activeTab: 'devices' } })}
              style={{ marginTop: '20px' }}
            >
              ← Torna ai Devices
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2 style={{ color: '#ffc107', marginBottom: '20px' }}>⚠️ Nessun Dato</h2>
            <p>Dati di salute non disponibili per questo device</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/admin', { state: { activeTab: 'devices' } })}
              style={{ marginTop: '20px' }}
            >
              ← Torna ai Devices
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px 20px', maxWidth: '1200px' }}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
        <span
          onClick={() => navigate('/admin', { state: { activeTab: 'devices' } })}
          style={{ cursor: 'pointer', color: '#007bff' }}
        >
          Devices
        </span>
        {' > '}
        <span style={{ color: '#333' }}>
          {deviceInfo?.label || deviceInfo?.setupCode || deviceId}
        </span>
        {' > '}
        <span style={{ fontWeight: 600 }}>Health Status</span>
      </div>

      {/* Header with Device Info */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>
              🏥 Device Health Status
            </h1>
            {deviceInfo && (
              <div style={{ color: '#666', fontSize: '14px' }}>
                <strong>{deviceInfo.label}</strong>
                {' · '}
                <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
                  {deviceInfo.setupCode}
                </code>
                {deviceInfo.tenant && (
                  <>
                    {' · '}
                    <span>{deviceInfo.tenant.company_name}</span>
                  </>
                )}
                {deviceInfo.venue && (
                  <>
                    {' · '}
                    <span>{deviceInfo.venue.name}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <DeviceHealthBadge health={health} showLabel={true} />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Ultimo aggiornamento: {lastUpdate ? lastUpdate.toLocaleTimeString('it-IT') : 'N/A'}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (30s)
            </label>
          </div>
        </div>
      </div>

      {/* Overall Status Summary */}
      <div className="card" style={{ marginBottom: '20px', backgroundColor: '#f8f9fa', border: '2px solid #dee2e6' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#2c3e50' }}>📊 Stato Generale</h2>
          <div style={{
            fontSize: '13px',
            backgroundColor: '#fff',
            padding: '8px 14px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <strong style={{ fontSize: '14px', color: '#2c3e50' }}>{health?.deviceLabel || 'Device'}</strong>
            <span style={{ margin: '0 6px', color: '#999' }}>•</span>
            <code style={{
              backgroundColor: '#f0f0f0',
              padding: '3px 8px',
              borderRadius: '4px',
              fontWeight: 600,
              fontSize: '12px',
              color: '#495057'
            }}>
              {health?.setupCode}
            </code>
            {health?.venue && (
              <>
                <span style={{ margin: '0 6px', color: '#999' }}>•</span>
                <span style={{ color: '#666' }}>📍 {health.venue}</span>
              </>
            )}
          </div>
        </div>

        {/* Prima riga: Status principali */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '15px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '14px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Overall Health</div>
            <div style={{ fontSize: '18px', fontWeight: 600, cursor: 'help' }}>
              <DeviceHealthBadge health={health} showLabel={true} />
            </div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            padding: '14px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>🔌 WebSocket</div>
            <div
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: getConnectionStatusColor(health.websocket?.status),
                cursor: 'help'
              }}
              title={getConnectionStatusTooltip(health.websocket?.status)}
            >
              {health.websocket?.status || 'UNKNOWN'}
            </div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            padding: '14px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>🌐 API</div>
            <div
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: getConnectionStatusColor(health.api?.status),
                cursor: 'help'
              }}
              title={getConnectionStatusTooltip(health.api?.status)}
            >
              {health.api?.status || 'UNKNOWN'}
            </div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            padding: '14px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>🎯 Lettore</div>
            <div style={{
              fontSize: '16px',
              fontWeight: 700,
              color: health.reader?.connected ? '#28a745' : '#dc3545'
            }}>
              {health.reader?.connected ? '✓ Connesso' : '✗ Disconnesso'}
            </div>
          </div>
        </div>

        {/* Seconda riga: Hardware */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          paddingTop: '15px',
          borderTop: '2px solid #dee2e6'
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '14px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>🔋 Batteria</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#2c3e50' }}>
              {health.hardware?.batteryLevel !== null && health.hardware?.batteryLevel !== undefined
                ? `${health.hardware.batteryLevel}%`
                : 'N/A'}
            </div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            padding: '14px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>📶 WiFi Signal</div>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>
              {health.hardware?.wifiStrength ? (
                <>
                  <span style={{ color: getWifiSignalQuality(health.hardware.wifiStrength).color }}>
                    {getWifiSignalQuality(health.hardware.wifiStrength).label}
                  </span>
                  {' '}
                  <span style={{ fontSize: '12px', color: '#999', fontWeight: 400 }}>
                    ({health.hardware.wifiStrength} dBm)
                  </span>
                </>
              ) : <span style={{ color: '#6c757d' }}>N/A</span>}
            </div>
          </div>
        </div>
      </div>

      {/* WebSocket Status */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>🔌 WebSocket Status</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canSendMessage() && (
              <>
                <button
                  className="btn btn-small"
                  onClick={handleOpenMessageModal}
                  style={{
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    fontSize: '12px',
                    padding: '6px 12px'
                  }}
                  title="Invia messaggio display via WebSocket"
                >
                  💬 Invia Messaggio
                </button>
                <button
                  className="btn btn-small"
                  onClick={handleRebootDevice}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    fontSize: '12px',
                    padding: '6px 12px'
                  }}
                  title="Riavvia il tablet da remoto"
                >
                  🔄 Riavvia Tablet
                </button>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ color: '#666' }}>Status:</span>
            <span
              style={{
                fontWeight: 600,
                color: getConnectionStatusColor(health.websocket?.status),
                cursor: 'help'
              }}
              title={getConnectionStatusTooltip(health.websocket?.status)}
            >
              {health.websocket?.status || 'UNKNOWN'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ color: '#666' }}>Ultimo Heartbeat:</span>
            <span style={{ fontWeight: 600 }}>
              {formatTimestamp(health.websocket?.lastSeenAt)}
              {health.websocket?.lastSeenAt && (
                <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                  ({formatDuration(health.websocket?.lastSeenAt)})
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* API Status */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>🌐 API Status</h2>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ color: '#666' }}>Status:</span>
            <span
              style={{
                fontWeight: 600,
                color: getConnectionStatusColor(health.api?.status),
                cursor: 'help'
              }}
              title={getConnectionStatusTooltip(health.api?.status)}
            >
              {health.api?.status || 'UNKNOWN'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ color: '#666' }}>Ultimo Heartbeat API:</span>
            <span style={{ fontWeight: 600 }}>
              {formatTimestamp(health.api?.lastSeenAt)}
              {health.api?.lastSeenAt && (
                <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                  ({formatDuration(health.api?.lastSeenAt)})
                </span>
              )}
            </span>
          </div>
          {health.api?.lastEndpoint && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Ultimo Endpoint:</span>
              <code style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', borderRadius: '3px', fontSize: '12px' }}>
                {health.api?.lastMethod} {health.api?.lastEndpoint}
              </code>
            </div>
          )}
        </div>
      </div>

      {/* Reader Status */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>💳 Stripe Terminal Reader</h2>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ color: '#666' }}>Stato Connessione:</span>
            <span style={{ fontWeight: 600 }}>
              {health.reader?.connected ? '🟢 Connesso' : '🔴 Disconnesso'}
            </span>
          </div>
          {health.reader && (
            <>
              {health.reader.label && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ color: '#666' }}>Modello:</span>
                  <span style={{ fontWeight: 600 }}>{health.reader.label}</span>
                </div>
              )}
              {health.reader.serialNumber && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ color: '#666' }}>Serial Number:</span>
                  <code style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', borderRadius: '3px', fontSize: '12px' }}>
                    {health.reader.serialNumber}
                  </code>
                </div>
              )}
              {health.reader.lastSeenAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ color: '#666' }}>Ultimo Aggiornamento:</span>
                  <span style={{ fontSize: '13px' }}>
                    {formatTimestamp(health.reader.lastSeenAt)}
                    <span style={{ color: '#999', fontSize: '11px', marginLeft: '6px' }}>
                      ({formatDuration(health.reader.lastSeenAt)})
                    </span>
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* App Information */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>📱 Informazioni App</h2>
          <button
            className="btn btn-small"
            onClick={handleForceUpdateCheck}
            disabled={!canSendMessage() || forcingUpdateCheck}
            style={{
              backgroundColor: canSendMessage() ? '#6f42c1' : '#adb5bd',
              color: 'white',
              fontSize: '12px',
              padding: '6px 12px',
              cursor: canSendMessage() ? 'pointer' : 'not-allowed',
              opacity: forcingUpdateCheck ? 0.8 : 1,
            }}
            title={
              canSendMessage()
                ? 'Invia un comando al device per forzare il check aggiornamenti'
                : 'Device offline su WebSocket: non posso inviare comandi'
            }
          >
            {forcingUpdateCheck ? '⏳ Forzando...' : '🔄 Forza Check Update'}
          </button>
        </div>
        <div style={{ display: 'grid', gap: '12px' }}>
          {health.app?.version && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Versione App:</span>
              <code style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', borderRadius: '3px', fontSize: '12px' }}>
                {health.app.version}
              </code>
            </div>
          )}
          {health.app?.platform && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Piattaforma:</span>
              <span style={{ fontWeight: 600 }}>{health.app.platform}</span>
            </div>
          )}
          {health.app?.osVersion && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Versione Sistema:</span>
              <span style={{ fontWeight: 600 }}>{health.app.osVersion}</span>
            </div>
          )}
        </div>
      </div>

      {/* Hardware Information */}
      {(health.app?.deviceModel || health.hardware?.screenResolution) && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>🖥️ Informazioni Hardware</h2>
          <div style={{ display: 'grid', gap: '12px' }}>
            {health.app?.manufacturer && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <span style={{ color: '#666' }}>Produttore:</span>
                <span style={{ fontWeight: 600 }}>{health.app.manufacturer}</span>
              </div>
            )}
            {health.app?.deviceModel && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <span style={{ color: '#666' }}>Modello:</span>
                <span style={{ fontWeight: 600 }}>{health.app.deviceModel}</span>
              </div>
            )}
            {health.hardware?.screenResolution && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <span style={{ color: '#666' }}>Risoluzione Schermo:</span>
                <code style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', borderRadius: '3px', fontSize: '12px' }}>
                  {health.hardware.screenResolution}
                </code>
              </div>
            )}
            {health.hardware?.batteryLevel !== null && health.hardware?.batteryLevel !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <span style={{ color: '#666' }}>Livello Batteria:</span>
                <span style={{ fontWeight: 600 }}>{health.hardware.batteryLevel}%</span>
              </div>
            )}
            {health.hardware?.wifiStrength !== null && health.hardware?.wifiStrength !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <span style={{ color: '#666' }}>Segnale WiFi:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '16px' }}>
                    {getWifiSignalQuality(health.hardware.wifiStrength).icon}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>
                    {getWifiSignalQuality(health.hardware.wifiStrength).label}
                  </span>
                  <span style={{ fontSize: '12px', color: '#999', fontWeight: 600 }}>
                    ({health.hardware.wifiStrength} dBm)
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Errors */}
      {health.health?.lastError && (
        <div className="card" style={{ marginBottom: '20px', borderLeft: '4px solid #dc3545' }}>
          <h2 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#dc3545' }}>⚠️ Ultimo Errore</h2>
          <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px', fontSize: '13px' }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Timestamp:</strong> {formatTimestamp(health.health?.lastErrorAt)}
            </div>
            <div style={{ fontFamily: 'monospace', color: '#856404' }}>
              {health.health?.lastError}
            </div>
          </div>
        </div>
      )}

      {/* Health Status Legend */}
      <HealthStatusLegend />

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/admin', { state: { activeTab: 'devices' } })}
        >
          ← Torna ai Devices
        </button>
        <button
          className="btn btn-primary"
          onClick={() => loadHealthData()}
        >
          🔄 Ricarica Dati
        </button>
      </div>

      {/* Modal Invio Messaggio */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3>💬 Invia Messaggio a {health?.deviceLabel || 'Device'}</h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Titolo *
              </label>
              <input
                type="text"
                value={messageForm.title}
                onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value })}
                placeholder="Es: Stiamo chiudendo"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Messaggio *
              </label>
              <textarea
                value={messageForm.message}
                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                placeholder="Es: Il locale chiuderà tra 10 minuti"
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Tipo Messaggio
              </label>
              <select
                value={messageForm.type}
                onChange={(e) => setMessageForm({ ...messageForm, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="info">ℹ️ Info (Blu)</option>
                <option value="warning">⚠️ Warning (Arancio)</option>
                <option value="error">❌ Error (Rosso)</option>
                <option value="success">✅ Success (Verde)</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Durata (secondi, 0 = infinito)
              </label>
              <input
                type="number"
                value={messageForm.duration}
                onChange={(e) => setMessageForm({ ...messageForm, duration: parseInt(e.target.value) || 0 })}
                min="0"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={messageForm.dismissible}
                  onChange={(e) => setMessageForm({ ...messageForm, dismissible: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <span>Utente può chiudere il messaggio</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCloseMessageModal}
                disabled={sendingMessage}
                className="btn btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageForm.title || !messageForm.message}
                className="btn"
                style={{
                  backgroundColor: sendingMessage ? '#6c757d' : '#007bff',
                  color: 'white'
                }}
              >
                {sendingMessage ? '⏳ Invio...' : '📤 Invia Messaggio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceHealthDetail;
